import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Space, Text } from '@cashfree-intl/coherent';

import { SubStepDescription } from 'containers/CheckoutJourney/screens/styled';
import AddTransactionWebhookModal from 'containers/Configuration/components/AddPaymentGatewayModal';
import { GUIDE_URL, HERE_GUIDE_URL, INTEGRATION_STATUS } from 'containers/CheckoutJourney/util/constant';
import { sendAnalyticsData } from 'containers/ProductTour/helper';

import { useInjectReducer } from 'redux-injectors';
import { useInjectSaga } from 'utils/injectSaga';
import WebHookReducer from 'containers/Configuration/reducer';
import WebHookSaga from 'containers/Configuration/saga';

import {
  testWebhookAction,
  getAllNotificationTypesAction,
  enableLoaderAction,
  disableLoaderAction,
  createBulkNotificationChannelAction,
} from 'containers/Configuration/actions';
import { SubstepComponentProps } from 'containers/CheckoutJourney/types';


const UpdateWebhookUrl = ({ substep, updateStatus }: SubstepComponentProps) => {
  useInjectReducer({ key: 'webhookNotifications', reducer: WebHookReducer });
  useInjectSaga({ key: 'webhookNotifications', saga: WebHookSaga, mode: ''  });

  const dispatch = useDispatch();
  const isLoading: boolean = useSelector(
    (state: { webhookNotifications?: { isLoading?: boolean } }) =>
      state.webhookNotifications?.isLoading || false
  );

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [testWebhookData, setTestWebhookData] = useState<Record<string, any>>(
    {}
  );
  const [editEventsList, setEditEventsList] = useState<any[]>([]);
  const [actionsMode, setActionsMode] = useState<string | undefined>();
  const [inactiveEvents, setInactiveEvents] = useState<string[]>([]);

  const testWebhook = (data: any) => dispatch(testWebhookAction(data));
  const getAllNotificationTypes = (data: any) =>
    dispatch(getAllNotificationTypesAction(data));
  const enableLoader = () => dispatch(enableLoaderAction());
  const disableLoader = () => dispatch(disableLoaderAction());
  const createBulkNotificationChannelDetails = (data: any) =>
    dispatch(createBulkNotificationChannelAction(data));

  const onGuideClick = () => window.open(GUIDE_URL);
  const onHereClick = () => window.open(HERE_GUIDE_URL);

  const closeModal = () => {
    if (openModal) {
      setTestWebhookData({});
      setEditEventsList([]);
    }
    setOpenModal(false);
    setActionsMode(undefined);
  };

  const filterNewEvent = (data: { notificationType: string }[]) =>
    data.filter((item) => !inactiveEvents.includes(item.notificationType));

  const handleTransactionFormSubmit = ({
    data,
    mode,
  }: {
    data: any;
    mode: string;
  }) => {
    enableLoader();
    const newEvents = filterNewEvent(data);
    createBulkNotificationChannelDetails({
      data: newEvents,
      cb: () => {
        disableLoader();
        closeModal();
      },
      mode,
    });
  };

  const onClickConfigureWebhooks = () => setOpenModal(true);

  const markAsDone = () => {
    updateStatus(substep.id, INTEGRATION_STATUS.COMPLETED);
    sendAnalyticsData('updateWebhookUrlButtonClicked');
  };

  const onClickWilldoLater = () => {
    updateStatus(substep.id, INTEGRATION_STATUS.PENDING);
  };

  return (
    <Space gap={2.5} direction="column" className="mt-2">
      <Space direction="column">
        <SubStepDescription color="bodyLight">
          Receive real-time events on your webhook endpoint so your integration
          can automatically trigger updates.
        </SubStepDescription>
        <SubStepDescription color="bodyLight">
          Read this{' '}
          <Text
            as="span"
            color="primary"
            onClick={onGuideClick}
            style={{ cursor: 'pointer' }}
          >
            guide
          </Text>{' '}
          to configure webhooks with Cashfree & know about best practices{' '}
          <Text
            as="span"
            color="primary"
            onClick={onHereClick}
            style={{ cursor: 'pointer' }}
          >
            here
          </Text>
          .
        </SubStepDescription>
      </Space>
      <Space gap={1}>
        <Button
          primary
          onClick={onClickConfigureWebhooks}
          data-event-name="Button_ConfigureWebhooks"
        >
          Configure Webhooks
        </Button>
        <Button
          link
          onClick={onClickWilldoLater}
          data-event-name="Button_DoLater"
        >
          I will do later
        </Button>
      </Space>

      {openModal && (
        <AddTransactionWebhookModal
          open
          title="Add Webhook Endpoint"
          closeModal={closeModal}
          getAllNotificationTypes={getAllNotificationTypes}
          testWebhook={testWebhook}
          handleTransactionFormSubmit={handleTransactionFormSubmit}
          actionsMode={actionsMode}
          testWebhookData={testWebhookData}
          editEventsList={editEventsList}
          isLoading={isLoading}
          markAsDone={markAsDone}
        />
      )}
    </Space>
  );
};

export { UpdateWebhookUrl };
